<template>
  <div class="task-comments">
    <div class="task-comments__list">
      <div v-if="loading" class="text-center text-muted py-4">
        <b-spinner small class="mr-2"></b-spinner> Cargando comentarios...
      </div>
      <template v-else>
        <div v-for="row in comments" :key="row.message_id" class="task-comments__item">
          <b-avatar size="2rem" :src="row.other_staff_image" :text="avatarInitial(row.other_staff_name)" variant="light" class="mr-2"></b-avatar>
          <div class="task-comments__item-body">
            <div class="d-flex justify-content-between align-items-baseline">
              <span class="task-comments__item-name">Con {{ row.other_staff_name || 'alguien' }}</span>
              <span class="task-comments__item-time" v-b-tooltip.hover :title="formatFullDate(row.created_at)">{{ formatDate(row.created_at) }}</span>
            </div>
            <div class="task-comments__item-text">
              <MessageBody :body="displayBody(row.body)" />
            </div>
          </div>
        </div>

        <EmptyState v-if="!comments.length"
                    icon="chat_bubble"
                    title="Sin comentarios"
                    description="Todavía no se dejaron comentarios sobre esta tarea." />
      </template>
    </div>

    <div class="task-comments__composer">
      <div class="task-comments__recipients">
        <span class="task-comments__recipients-label">Notificar a:</span>
        <b-button v-for="person in recipients"
                  :key="'recipient-' + person.id"
                  size="sm"
                  :variant="isSelected(person) ? 'primary' : 'outline-secondary'"
                  class="task-comments__recipient-btn"
                  @click="selectRecipient(person)">
          <b-avatar size="1.25rem" :src="person.image" :text="avatarInitial(person.name)" class="mr-1"></b-avatar>
          {{ person.name }}
        </b-button>
        <span v-if="!recipients.length" class="text-muted small">Sin personas para notificar</span>
      </div>

      <b-form-textarea v-model="commentText"
                        placeholder="Escribí un comentario..."
                        rows="2"
                        max-rows="6"
                        :disabled="sending"></b-form-textarea>

      <div class="d-flex justify-content-between align-items-center mt-2">
        <small class="text-muted" v-if="selectedRecipient">
          Se va a notificar a <strong>{{ selectedRecipient.name }}</strong>
        </small>
        <small class="text-muted" v-else>Elegí a quién notificar</small>

        <b-button variant="primary"
                  size="sm"
                  :disabled="!canSend"
                  @click="send()">
          <b-spinner small v-if="sending" class="mr-1"></b-spinner>
          <AppIcon v-else name="send" class="mr-1"></AppIcon>
          Enviar
        </b-button>
      </div>
    </div>
  </div>
</template>
<script>
import moment from 'moment'
import MessageBody from '@/components/inc/messages/MessageBody'
import { buildMentionToken } from '@/handler/mentions'
import messagesServiceAPI from './services'
import tasksServiceAPI from '@/components/modules/users/tasks/staff/services'
import ErrorToken from '@/handler/errorToken'

// Comentarios de una tarea, reusando el módulo de Mensajería Interna: cada
// comentario es un mensaje 1-a-1 normal (con su notificación ya resuelta por
// ese módulo) al que se le agrega el token @[Título](task:id) al final del
// texto -- eso es lo que permite después traerlos todos juntos acá vía
// MessagesController::searchReferences('task', taskId).
//
// Ojo: como las conversaciones son 1-a-1, esta lista solo puede mostrar los
// comentarios de conversaciones donde el staff logueado participa (mismo
// alcance que la pantalla de "buscar por referencia" del inbox) -- no es un
// muro público visible para cualquiera que abra la tarea.
export default {
  name: 'TaskComments',
  components: {
    MessageBody,
  },
  props: {
    taskId: {
      type: [Number, String],
      required: true,
    },
    taskTitle: {
      type: String,
      required: true,
    },
    creator: {
      type: Object,
      default: null,
    },
    assigned: {
      type: Object,
      default: null,
    },
    currentStaffId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      comments: [],
      commentText: '',
      selectedRecipient: null,
      projectStaff: [],
      sending: false,
    }
  },
  computed: {
    // creador/asignado son el fallback para tareas sin proyecto; el staff
    // del proyecto (project manager + equipo) es el conjunto principal
    // cuando la tarea sí tiene uno. Sin duplicados ni el propio staff
    // logueado (no se puede iniciar una conversación con uno mismo).
    recipients() {
      var people = [this.creator, this.assigned].concat(this.projectStaff).filter(function (person) {
        return !!(person && person.id)
      })

      var seen = {}
      var result = []
      people.forEach(function (person) {
        if (seen[person.id]) return
        if (this.currentStaffId && Number(person.id) === Number(this.currentStaffId)) return
        seen[person.id] = true
        result.push(person)
      }, this)

      return result
    },
    canSend() {
      return !this.sending && !!this.selectedRecipient && !!(this.commentText && this.commentText.trim())
    },
  },
  mounted() {
    this.loadComments()
    this.loadProjectStaff()
  },
  methods: {
    // el body guardado arranca con el token @[Título](task:id) para que
    // parseMentions() lo indexe -- acá ya están agrupados bajo la tarea, así
    // que ese chip es redundante en cada fila: se muestra solo el comentario.
    displayBody(body) {
      return (body || '').replace(/^@\[[^\]]+\]\(task:\d+\)\n?/, '')
    },
    avatarInitial(name) {
      return name ? name.charAt(0).toUpperCase() : '?'
    },
    formatDate(date) {
      return moment(date).fromNow()
    },
    formatFullDate(date) {
      return moment(date).format('DD/MM/YYYY HH:mm')
    },
    isSelected(person) {
      return !!(this.selectedRecipient && Number(this.selectedRecipient.id) === Number(person.id))
    },
    selectRecipient(person) {
      this.selectedRecipient = person
    },
    loadProjectStaff() {
      tasksServiceAPI.obtenerStaffDelProyecto(this.taskId)
        .then((response) => {
          this.projectStaff = response.data || []
        })
        .catch(() => {
          // silencioso: sin proyecto o sin permiso, simplemente no suma chips extra.
        })
    },
    loadComments() {
      this.loading = true
      messagesServiceAPI.searchReferences('task', this.taskId)
        .then((response) => {
          this.comments = response.data || []
        })
        .catch((error) => {
          this.$awn.alert(ErrorToken.valid(error))
        })
        .finally(() => {
          this.loading = false
        })
    },
    send() {
      if (!this.canSend) return

      this.sending = true
      var recipient = this.selectedRecipient
      var body = buildMentionToken(this.taskTitle, 'task', this.taskId) + '\n' + this.commentText.trim()

      messagesServiceAPI.startConversation(recipient.id)
        .then((response) => {
          var conversationId = response.data.id
          return messagesServiceAPI.sendMessage(conversationId, body)
        })
        .then((response) => {
          this.comments.unshift({
            message_id: response.data.id,
            body: body,
            created_at: response.data.created_at,
            conversation_id: response.data.messages_conversation_id,
            other_staff_id: recipient.id,
            other_staff_name: recipient.name,
            other_staff_image: recipient.image,
          })
          this.commentText = ''
        })
        .catch((error) => {
          this.$awn.alert(ErrorToken.valid(error))
        })
        .finally(() => {
          this.sending = false
        })
    },
  },
}
</script>
<style scoped>
.task-comments__list {
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 14px;
  padding-right: 4px;
}
.task-comments__item {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid var(--app-color-border);
}
.task-comments__item:last-child {
  border-bottom: none;
}
.task-comments__item-body {
  flex: 1;
  min-width: 0;
}
.task-comments__item-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--app-color-text-secondary);
}
.task-comments__item-time {
  font-size: 0.75rem;
  color: var(--app-color-text-muted);
  white-space: nowrap;
}
.task-comments__item-text {
  font-size: 0.88rem;
  word-break: break-word;
  white-space: pre-wrap;
}
.task-comments__composer {
  border-top: 1px solid var(--app-color-border);
  padding-top: 12px;
}
.task-comments__recipients {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}
.task-comments__recipients-label {
  font-size: 0.8rem;
  color: var(--app-color-text-muted);
  margin-right: 4px;
}
.task-comments__recipient-btn {
  display: inline-flex;
  align-items: center;
}
</style>
